/**
 * 이모 수첩 화면 조각 (시안 04 DS-2).
 *
 * - MemoLine(D2a): 대화 안 가운데 알약 「이모 수첩에 적었어요 · 고치기」. 같은 대화에서 하루 3번까지(서버가 셈)
 * - MemoEditSheet(D2b): 항목 고치기 시트. 열자마자 커서, 「고친 대로 적기」(바뀐 게 없으면 꺼짐) · 「이 줄 지우기」
 * - MemoItem(D2c · D2e): 수첩 항목 한 줄(본문 · 날짜 · 고치기 · 지우기)
 * - MemoToast: 「지웠어요 · 되돌리기」 5초
 * - 단골이 직접 적은 줄(시안 13, C-31): 날짜 앞 「내가 적음」 꼬리표, 일정이면 「· 일정 11월 2일」, 고치기는 적기 시트(NoteSheet)
 */
import { useEffect, useRef, useState } from 'react';
import { NotebookPen } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import * as meApi from '../../api/me.js';
import { errorMessageOf } from '../../api/client.js';
import { useMemoBook as useMemoStore } from '../../stores/memo.js';
import { useNoteSheet } from '../../stores/noteSheet.js';
import { BottomSheet } from '../BottomSheet.jsx';
import { Button } from '../Button.jsx';

function shortDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const parts = new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric' })
        .formatToParts(date)
        .reduce((all, part) => ({ ...all, [part.type]: part.value }), {});
    return `${parts.month}월 ${parts.day}일`;
}

/** 출처 · 범위 줄: 「별채대화에서 · 멤버 모두 알아요」 / 「오름과의 1:1에서 · 오름만 알아요」 */
export function memoSourceOf(memory) {
    if (memory.scope === 'chat' && memory.memberKey) {
        const name = getMember(memory.memberKey)?.name ?? '';
        return `${withJosa(name, '과', '와')}의 1:1에서 · ${name}만 알아요`;
    }
    return '별채대화에서 · 멤버 모두 알아요';
}

/** 예전에 「단골은 …」으로 적힌 줄은 주어를 빼고 보입니다(시안 04 D2c 「유자차를 좋아한다.」) */
export function displayMemoText(text) {
    return String(text ?? '').replace(/^단골은\s+/, '');
}

/** 고친 문장이 가게에 있으면 그것으로 */
export function useMemoText(memory) {
    const edited = useMemoStore((state) => state.editedText[memory?.memoriesId ?? memory?.id]);
    return displayMemoText(edited ?? memory?.text ?? '');
}

/** 일정 날짜 「11월 2일」 */
function dueDateText(dueOn) {
    const [, month, day] = String(dueOn ?? '').split('-');
    return month && day ? `${Number(month)}월 ${Number(day)}일` : '';
}

export function MemoLine({ onEdit, byUser = false }) {
    return (
        <div className="mt-2.5 flex justify-center">
            <span className="flex h-[30px] items-center gap-1.5 rounded-[15px] bg-surface-sunken pr-1 pl-3 text-[12px] text-text-secondary">
                <NotebookPen size={14} strokeWidth={1.6} aria-hidden="true" />
                {byUser ? '내가 수첩에 적었어요' : '이모 수첩에 적었어요'}
                <span aria-hidden="true">·</span>
                <button
                    type="button"
                    onClick={onEdit}
                    className="flex min-h-touch items-center px-2 font-semibold text-brand"
                    aria-label="이모 수첩에 적은 말 고치기"
                >
                    고치기
                </button>
            </span>
        </div>
    );
}

/**
 * @param {object|null} memory  { memoriesId, text, scope, memberKey, createdAt } (열 때만)
 */
export function MemoEditSheet({ memory, onClose }) {
    const currentText = useMemoText(memory);
    const [text, setText] = useState(currentText);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    const noteEdited = useMemoStore((state) => state.noteEdited);
    const deleteWithUndo = useMemoStore((state) => state.deleteWithUndo);

    useEffect(() => {
        setText(currentText);
        setError(null);
        if (memory) {
            requestAnimationFrame(() => {
                const input = inputRef.current;
                if (input) {
                    input.focus();
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            });
        }
        // 열 때마다 처음 글로
    }, [memory?.memoriesId]);

    if (!memory) {
        return null;
    }
    const changed = text.trim() && text.trim() !== currentText.trim();

    async function save() {
        setBusy(true);
        setError(null);
        try {
            const result = await meApi.editMemory(memory.memoriesId, text.trim());
            noteEdited(memory.memoriesId, result.memory.text);
            onClose();
        } catch (requestError) {
            setError(errorMessageOf(requestError, '고치지 못했어요. 잠시 후 다시 해 주세요.'));
        } finally {
            setBusy(false);
        }
    }

    function remove() {
        deleteWithUndo(memory);
        onClose();
    }

    return (
        <BottomSheet open onClose={onClose} label="이모 수첩 고치기">
            <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2.5">
                    <NotebookPen size={22} strokeWidth={1.6} aria-hidden="true" className="mt-0.5 text-brand" />
                    <div className="flex flex-col">
                        <h2 className="m-0 text-[17px] leading-6 font-bold">이모 수첩</h2>
                        <span className="text-[12px] leading-4 text-text-secondary">
                            {memoSourceOf(memory)}
                            {memory.createdAt ? ` · ${shortDate(memory.createdAt)}` : ''}
                        </span>
                    </div>
                </div>
                <label className="flex flex-col gap-1.5">
                    <span className="text-section text-label-muted">적어 둔 말</span>
                    <textarea
                        ref={inputRef}
                        value={text}
                        maxLength={200}
                        onChange={(event) => setText(event.target.value)}
                        className="min-h-[72px] resize-none rounded-xl border-[1.5px] border-transparent bg-bg px-3.5 py-3 font-serif text-[16px] leading-[26px] outline-none focus:border-brand"
                    />
                </label>
                <p className="m-0 text-[12px] leading-4 text-text-secondary">고친 문장으로 다음 대화부터 기억해요.</p>
                {error ? (
                    <p role="alert" className="m-0 text-caption text-error">
                        {error}
                    </p>
                ) : null}
                <Button size="lg" disabled={!changed} busy={busy} onClick={save}>
                    고친 대로 적기
                </Button>
                <button
                    type="button"
                    onClick={remove}
                    className="h-12 rounded-card text-[15px] font-semibold text-error"
                >
                    이 줄 지우기
                </button>
            </div>
        </BottomSheet>
    );
}

export function MemoItem({ memory, onEdit }) {
    const text = useMemoText(memory);
    const deleteWithUndo = useMemoStore((state) => state.deleteWithUndo);
    // 시점 이력(C-27 · D69): 바뀌기 전 값은 펼치면 보입니다(지금 값만 먼저)
    const [historyOpen, setHistoryOpen] = useState(false);
    const history = memory.history ?? [];
    const openNoteSheet = useNoteSheet((state) => state.open);
    // 방금 적은 줄은 바탕이 잠깐(600ms) 짙었다 사라집니다(토스트 없음, 시안 13 3장)
    const lastSaved = useNoteSheet((state) => state.lastSaved);
    const [fresh, setFresh] = useState(
        () => lastSaved?.memoriesId === memory.memoriesId && Date.now() - lastSaved.at < 5000
    );
    useEffect(() => {
        if (!fresh) {
            return undefined;
        }
        const timer = setTimeout(() => setFresh(false), 600);
        return () => clearTimeout(timer);
    }, [fresh]);
    function edit() {
        if (memory.byUser) {
            openNoteSheet({ editing: memory, entry: 'me_tab', key: `edit-${memory.memoriesId}` });
            return;
        }
        onEdit(memory);
    }
    return (
        <li
            className={`flex flex-col gap-1 border-t border-hairline py-3 transition-colors duration-[600ms] ${
                fresh ? 'bg-surface-sunken' : ''
            }`}
        >
            <p className="m-0 font-serif text-[16px] leading-[26px]">
                {memory.byUser ? <span className="sr-only">내가 적음, </span> : null}
                {text}
            </p>
            {historyOpen && history.length ? (
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                    {history.map((item) => (
                        <li
                            key={`${item.createdAt}-${item.text}`}
                            className="text-[13px] leading-[18px] text-text-secondary line-through decoration-hairline"
                        >
                            {item.text} <span className="no-underline">· {shortDate(item.validTo)}까지</span>
                        </li>
                    ))}
                </ul>
            ) : null}
            <div className="flex items-center gap-3">
                {memory.byUser ? (
                    <span
                        aria-hidden="true"
                        className="flex h-5 items-center rounded-[10px] border border-hairline bg-bg px-2 text-[11px] font-semibold text-brand"
                    >
                        내가 적음
                    </span>
                ) : null}
                <span className="text-[12px] leading-4 text-label-muted">
                    {shortDate(memory.createdAt)}
                    {memory.firstVisit ? ' · 첫 방문' : ''}
                    {memory.kind === 'schedule' && memory.dueOn ? ` · 일정 ${dueDateText(memory.dueOn)}` : ''}
                </span>
                {history.length ? (
                    <button
                        type="button"
                        aria-expanded={historyOpen}
                        onClick={() => setHistoryOpen((open) => !open)}
                        className="min-h-touch text-[12px] font-semibold text-label-muted"
                    >
                        {historyOpen ? '이력 접기' : `바뀌기 전 ${history.length}`}
                    </button>
                ) : null}
                <span className="ml-auto flex">
                    <button
                        type="button"
                        onClick={edit}
                        className="min-h-touch px-2 text-[13px] font-semibold text-brand"
                    >
                        고치기
                    </button>
                    <button
                        type="button"
                        onClick={() => deleteWithUndo(memory)}
                        className="min-h-touch px-2 text-[13px] font-semibold text-text-secondary"
                    >
                        지우기
                    </button>
                </span>
            </div>
        </li>
    );
}

/** 「지웠어요 · 되돌리기」 (앱 어디서나 하나) */
export function MemoToast() {
    const toast = useMemoStore((state) => state.toast);
    const undo = useMemoStore((state) => state.undo);
    if (!toast) {
        return null;
    }
    return (
        <div
            role="status"
            className="fixed bottom-24 left-1/2 z-50 flex h-11 -translate-x-1/2 items-center gap-3 rounded-card bg-text-primary pr-1 pl-4 text-[14px] text-on-brand motion-safe:animate-message-in"
        >
            지웠어요
            <button type="button" onClick={undo} className="min-h-touch px-3 font-semibold text-brand-tint">
                되돌리기
            </button>
        </div>
    );
}
